import {
  addSysMLElement,
  addSysMLRelationship,
  createSysMLV2Model,
  deleteSysMLElement,
  deleteSysMLRelationship,
  ensureDefaultSysMLV2Model,
  loadSysMLV2Model,
  saveSysMLV2Model,
  updateSysMLElement,
  updateSysMLRelationship,
} from "./sysmlV2Store";
import { serializeSysMLV2Text } from "./sysmlV2Serializer";
import { validateSysMLV2Model } from "./sysmlV2Validator";
import { autoLayoutSysML } from "./sysmlV2DiagramUtils";

function resolveModel(args = {}) {
  return loadSysMLV2Model(args.modelId, args.projectId) || ensureDefaultSysMLV2Model(args.projectId);
}

function findElement(model, ref) {
  if (!ref) return null;
  return (model.elements || []).find((element) => element.id === ref || String(element.name).toLowerCase() === String(ref).toLowerCase()) || null;
}

function droneSeed(name = "DroneSystem") {
  return {
    modelName: name,
    elements: [
      { type: "Package", name, description: "Root package for a generic quadcopter drone." },
      { type: "PartDefinition", name: "FlightController", description: "Computes stabilization and motor commands.", ports: [{ name: "sensorData" }, { name: "motorCommands" }], metadata: { safetyCritical: true } },
      { type: "PartDefinition", name: "Battery", description: "Stores and supplies electrical power.", ports: [{ name: "powerOutput" }], metadata: { safetyCritical: true } },
      { type: "PartDefinition", name: "PowerDistribution", description: "Distributes regulated power to avionics and propulsion.", ports: [{ name: "powerInput" }, { name: "regulatedPower" }] },
      { type: "PartDefinition", name: "MotorController", description: "Converts motor commands into motor drive signals.", ports: [{ name: "powerInput" }, { name: "motorDrive" }] },
      { type: "PartDefinition", name: "Motors", description: "Provide thrust for controlled flight." },
      { type: "PartDefinition", name: "Sensors", description: "Provide inertial, altitude, and navigation data." },
      { type: "PartDefinition", name: "CommunicationsModule", description: "Maintains command and telemetry links." },
      { type: "InterfaceDefinition", name: "PowerInterface", description: "Electrical power transfer interface." },
      { type: "InterfaceDefinition", name: "CommandTelemetryInterface", description: "Command and telemetry exchange interface." },
      { type: "ActionDefinition", name: "StabilizeFlight", description: "Stabilize vehicle attitude and altitude.", metadata: { inputs: ["sensorData"], outputs: ["motorCommands"], safetyCritical: true } },
      { type: "RequirementDefinition", name: "MaintainControlledFlight", description: "The system shall maintain controlled flight during nominal operating conditions." },
      { type: "RequirementDefinition", name: "ProtectPowerDistribution", description: "The system shall protect flight-critical electronics from unsafe power transients." },
      { type: "VerificationCase", name: "FlightControlVerification", description: "Verify controlled flight behavior under representative conditions." },
    ],
    relationships: [
      { type: "connects", sourceName: "Battery", targetName: "PowerDistribution", label: "supplies power" },
      { type: "connects", sourceName: "PowerDistribution", targetName: "MotorController", label: "regulated power" },
      { type: "connects", sourceName: "MotorController", targetName: "Motors", label: "drives" },
      { type: "connects", sourceName: "Sensors", targetName: "FlightController", label: "sensor data" },
      { type: "connects", sourceName: "FlightController", targetName: "MotorController", label: "motor commands" },
      { type: "satisfies", sourceName: "FlightController", targetName: "MaintainControlledFlight", label: "satisfies" },
      { type: "satisfies", sourceName: "PowerDistribution", targetName: "ProtectPowerDistribution", label: "satisfies" },
      { type: "verifies", sourceName: "FlightControlVerification", targetName: "MaintainControlledFlight", label: "verifies" },
      { type: "allocates", sourceName: "StabilizeFlight", targetName: "FlightController", label: "allocated to" },
    ],
  };
}

export function applyGeneratedSysMLModel(generated) {
  let model = createSysMLV2Model({ name: generated.modelName || "GeneratedSysMLModel", description: "Generated by xHandle Collaborator.", projectId: generated.projectId });
  const byName = new Map((model.elements || []).map((element) => [element.name, element.id]));
  (generated.elements || []).forEach((element) => {
    if (byName.has(element.name)) return;
    const result = addSysMLElement(model.id, element);
    model = result.model;
    byName.set(element.name, result.element.id);
  });
  [...(generated.requirements || []), ...(generated.verificationCases || [])].forEach((element) => {
    if (!element?.name || byName.has(element.name)) return;
    const result = addSysMLElement(model.id, element);
    model = result.model;
    byName.set(element.name, result.element.id);
  });
  (generated.relationships || []).forEach((relationship) => {
    const sourceId = relationship.sourceId || byName.get(relationship.sourceName);
    const targetId = relationship.targetId || byName.get(relationship.targetName);
    if (!sourceId || !targetId) return;
    model = addSysMLRelationship(model.id, { ...relationship, sourceId, targetId }).model;
  });
  const diagrams = { ...(model.diagrams || {}) };
  ["structure", "interface", "requirements", "verification", "safety"].forEach((viewId) => {
    diagrams[viewId] = { ...(diagrams[viewId] || {}), positions: autoLayoutSysML(model, viewId) };
  });
  model = { ...model, diagrams };
  return saveSysMLV2Model(model);
}

export function getSysMLV2AgentTools(z) {
  return [
    {
      name: "create_sysml_v2_model",
      description: "Create a new local SysML v2-style model. Use for MBSE/modeling requests.",
      schema: z.object({ projectId: z.string().optional(), name: z.string().optional(), description: z.string().optional(), template: z.string().optional() }),
      execute: async (_ctx, args) => {
        if (/drone|quadcopter|uav/i.test(`${args.name || ""} ${args.description || ""} ${args.template || ""}`)) {
          return { model: applyGeneratedSysMLModel({ ...droneSeed(args.name || "DroneSystem"), projectId: args.projectId }) };
        }
        return { model: createSysMLV2Model({ name: args.name || "SystemModel", description: args.description || "", projectId: args.projectId }) };
      },
    },
    {
      name: "add_sysml_v2_element",
      description: "Add a SysML v2-style element such as Package, PartDefinition, RequirementDefinition, ActionDefinition, InterfaceDefinition, VerificationCase, or AnalysisCase.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional(), type: z.string(), name: z.string(), description: z.string().optional(), metadata: z.record(z.any()).optional() }),
      execute: async (_ctx, args) => addSysMLElement(resolveModel(args).id, args),
    },
    {
      name: "update_sysml_v2_element",
      description: "Update an existing SysML v2-style element by id or name.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional(), elementRef: z.string(), patch: z.record(z.any()) }),
      execute: async (_ctx, args) => {
        const model = resolveModel(args);
        const element = findElement(model, args.elementRef);
        if (!element) throw new Error(`SysML element not found: ${args.elementRef}`);
        return { model: updateSysMLElement(model.id, element.id, args.patch), elementId: element.id };
      },
    },
    {
      name: "delete_sysml_v2_element",
      description: "Delete a SysML v2-style element and connected relationships. Use only after user approval.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional(), elementRef: z.string() }),
      execute: async (_ctx, args) => {
        const model = resolveModel(args);
        const element = findElement(model, args.elementRef);
        if (!element) throw new Error(`SysML element not found: ${args.elementRef}`);
        return { model: deleteSysMLElement(model.id, element.id), deletedElementId: element.id };
      },
    },
    {
      name: "add_sysml_v2_relationship",
      description: "Add a labeled SysML relationship such as connects, allocates, satisfies, verifies, derives, refines, traces, or contains.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional(), type: z.string(), sourceRef: z.string(), targetRef: z.string(), label: z.string().optional(), description: z.string().optional() }),
      execute: async (_ctx, args) => {
        const model = resolveModel(args);
        const source = findElement(model, args.sourceRef);
        const target = findElement(model, args.targetRef);
        if (!source || !target) throw new Error("Relationship source or target was not found.");
        return addSysMLRelationship(model.id, { type: args.type, sourceId: source.id, targetId: target.id, label: args.label, description: args.description });
      },
    },
    {
      name: "update_sysml_v2_relationship",
      description: "Update an existing SysML v2-style relationship.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional(), relationshipId: z.string(), patch: z.record(z.any()) }),
      execute: async (_ctx, args) => ({ model: updateSysMLRelationship(resolveModel(args).id, args.relationshipId, args.patch) }),
    },
    {
      name: "delete_sysml_v2_relationship",
      description: "Delete an existing SysML v2-style relationship. Use only after user approval.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional(), relationshipId: z.string() }),
      execute: async (_ctx, args) => ({ model: deleteSysMLRelationship(resolveModel(args).id, args.relationshipId) }),
    },
    {
      name: "generate_sysml_v2_from_text",
      description: "Generate a practical SysML v2-style model from natural-language design text.",
      schema: z.object({ projectId: z.string().optional(), text: z.string().optional(), modelName: z.string().optional() }),
      execute: async (_ctx, args) => {
        const seed = /drone|quadcopter|uav/i.test(args.text || args.modelName || "") ? droneSeed(args.modelName || "DroneSystem") : {
          modelName: args.modelName || "GeneratedSystemModel",
          elements: [
            { type: "Package", name: args.modelName || "GeneratedSystemModel" },
            { type: "PartDefinition", name: "System", description: args.text || "Generated system element." },
            { type: "RequirementDefinition", name: "DefineSystemBehavior", description: "The system shall perform the behavior described in the design narrative." },
            { type: "VerificationCase", name: "SystemBehaviorVerification", description: "Verify the generated system behavior." },
          ],
          relationships: [
            { type: "satisfies", sourceName: "System", targetName: "DefineSystemBehavior" },
            { type: "verifies", sourceName: "SystemBehaviorVerification", targetName: "DefineSystemBehavior" },
          ],
        };
        return { model: applyGeneratedSysMLModel({ ...seed, projectId: args.projectId }), warnings: ["Generated from supported deterministic extraction; review before baselining."] };
      },
    },
    {
      name: "validate_sysml_v2_model",
      description: "Validate the active SysML v2-style model and report MBSE quality gaps.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional() }),
      execute: async (_ctx, args) => {
        const model = resolveModel(args);
        return { modelId: model.id, findings: validateSysMLV2Model(model) };
      },
    },
    {
      name: "export_sysml_v2_text",
      description: "Export the active model to the supported SysML v2-style textual subset.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional() }),
      execute: async (_ctx, args) => {
        const model = resolveModel(args);
        return { modelId: model.id, text: serializeSysMLV2Text(model) };
      },
    },
    {
      name: "summarize_sysml_v2_model",
      description: "Summarize the active SysML v2-style model.",
      schema: z.object({ projectId: z.string().optional(), modelId: z.string().optional() }),
      execute: async (_ctx, args) => {
        const model = resolveModel(args);
        return {
          modelId: model.id,
          summary: `${model.name}: ${model.elements.length} elements, ${model.relationships.length} relationships, ${validateSysMLV2Model(model).length} validation findings.`,
        };
      },
    },
  ];
}
